import Modal from "@/components/shared/modal/modal";
import { useRootTypeContext } from "@/pages/configurations/rootTypes/context/RootTypeContext";
import {
	Button,
	CircularProgress,
	FormControl,
	FormGroup,
	FormLabel,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	Snackbar,
	Switch,
	TextField,
	Theme,
	Typography,
	createStyles,
	makeStyles,
} from "@material-ui/core";
import { Alert, Color } from "@material-ui/lab";
import React, { useEffect, useState } from "react";

import { ErrorModal } from "@/components/shared/modal/error-modal";
import { ComponentType } from "@/pages/configurations/componentTypes/models/ComponentType";
import { INITIAL_VALUES } from "@/pages/configurations/componentTypes/models/FormInitialValues";
import { createComponentType } from "@/pages/configurations/componentTypes/repositories/ComponentTypes";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		modalBody: {},
		title: {
			fontSize: 15,
		},
		checkbox: {
			userSelect: "none",
		},
		chip: {
			margin: 2,
		},
		errorLabel: {
			fontSize: 12,
			color: "#F44336",
		},
	}),
);

type Props = {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
};

export const CreateComponentType = ({ show, onHide }: Props) => {
	const [openAlertMessage, setOpenAlertMessage] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [message, setMessage] = useState("");
	const [messageType, setMessageType] = useState<Color | undefined>(undefined);

	const [error, setError] = useState<string | null>(null);
	const [showErrorModal, setShowErrorModal] = useState(false);

	const classes = useStyles();

	const {
		state: { rootTypes },
	} = useRootTypeContext();

	const {
		control,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<ComponentType>({ defaultValues: INITIAL_VALUES });

	const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
		if (reason === "clickaway") {
			return;
		}
		setOpenAlertMessage(false);
	};
	const onSubmit: SubmitHandler<ComponentType> = async (data) => {
		setIsSubmitLoading(true);
		try {
			const res = await createComponentType(data);
			if (res.ok) {
				setMessage("The component type was created successfully");
				setMessageType("success");
				onHide(true);
			}
		} catch (e) {
			setMessage(`Error, please try again`);
			setMessageType("error");

			setError(e as string);
			setShowErrorModal(true);
		}
		setIsSubmitLoading(false);
		setOpenAlertMessage(true);
	};

	useEffect(() => {
		if (show) {
			reset();
			setIsSubmitLoading(false);
		}
	}, [show]);

	return (
		<>
			<>
				<Modal
					open={show}
					handleClose={() => {
						onHide(true);
					}}
					id="createComponentType"
					title="Create a new component type"
					maxWidth="md"
				>
					<form onSubmit={handleSubmit(onSubmit)}>
						<Modal.Content>
							{isLoading && (
								<Grid
									container
									justifyContent="center"
									alignItems="center"
									style={{ minHeight: "300px" }}
								>
									{/* <LazyLoading /> */}
									<CircularProgress size="5rem" />
								</Grid>
							)}

							{!isLoading && (
								<>
									<Grid container spacing={4}>
										<Grid item md={3} xs={12}>
											<Controller
												name="componentTypeId"
												rules={{
													required: true,
													validate: { positive: (v: any) => v != 0 },
												}}
												control={control}
												render={({ field }) => (
													<TextField
														label="Component Type ID"
														value={field.value}
														onChange={field.onChange}
														variant="outlined"
														error={!!errors.componentTypeId}
														fullWidth
														margin="dense"
													/>
												)}
											/>
											{errors.componentTypeId?.type === "positive" && (
												<span className={classes.errorLabel}>
													Component Type ID must be different from 0
												</span>
											)}
											{errors.componentTypeId?.type === "required" && (
												<span className={classes.errorLabel}>
													Component Type ID is required
												</span>
											)}
										</Grid>
										<Grid item md={9} xs={12}>
											<Controller
												name="componentTypeName"
												rules={{ required: true }}
												control={control}
												render={({ field }) => (
													<TextField
														label="Name"
														value={field.value}
														onChange={field.onChange}
														variant="outlined"
														error={!!errors.componentTypeName}
														fullWidth
														margin="dense"
													/>
												)}
											/>
											{errors.componentTypeName?.type === "required" && (
												<span className={classes.errorLabel}>
													Name is required
												</span>
											)}
										</Grid>
										<Grid item md={6} xs={12}>
											<Controller
												name="rootTypeId"
												control={control}
												rules={{
													required: true,
													validate: { positive: (v) => v != 0 },
												}}
												render={({ field }) => (
													<FormControl fullWidth>
														<InputLabel
															variant="standard"
															htmlFor="uncontrolled-native"
														>
															Root type
														</InputLabel>
														<Select
															error={!!errors.rootTypeId}
															value={field.value}
															onChange={field.onChange}
														>
															{rootTypes.map((type) => {
																return (
																	<MenuItem
																		value={type.rootTypeId}
																		key={type.rootTypeId}
																	>
																		{type.rootTypeName}
																	</MenuItem>
																);
															})}
														</Select>
													</FormControl>
												)}
											/>
											{errors.rootTypeId?.type === "positive" && (
												<span className={classes.errorLabel}>
													This field is required
												</span>
											)}
										</Grid>
										<Grid item md={3} xs={6}>
											<Typography>Repair</Typography>
											<Controller
												name="isRepair"
												control={control}
												render={({ field }) => (
													<FormGroup>
														<Switch
															// className='switchBase'
															color="primary"
															defaultChecked={true}
															value={field.value}
															onChange={field.onChange}
														/>

														<FormLabel component="legend">
															{field.value ? "Yes" : "No"}
														</FormLabel>
													</FormGroup>
												)}
											/>
										</Grid>
										<Grid item md={3} xs={6}>
											<Typography>Tracked</Typography>
											<Controller
												name="isTracked"
												control={control}
												render={({ field }) => (
													<FormGroup>
														<Switch
															// className='switchBase'
															color="primary"
															defaultChecked={true}
															value={field.value}
															onChange={field.onChange}
														/>

														<FormLabel component="legend">
															{field.value ? "Yes" : "No"}
														</FormLabel>
													</FormGroup>
												)}
											/>
										</Grid>
									</Grid>
								</>
							)}
						</Modal.Content>
						<Modal.Actions>
							<Grid
								container
								spacing={2}
								justifyContent="flex-end"
								style={{ padding: "1rem" }}
							>
								<Grid item md={3} xs={12} style={{ margin: 0 }}>
									<Button
										fullWidth
										variant="contained"
										color="default"
										onClick={() => {
											onHide(false);
										}}
									>
										Cancel
									</Button>
								</Grid>
								<Grid item md={3} xs={12} style={{ margin: 0 }}>
									<Button
										fullWidth
										startIcon={
											isSubmitLoading ? <CircularProgress size="1rem" /> : <></>
										}
										disabled={isSubmitLoading}
										variant="contained"
										color="primary"
										type="submit"
									>
										Save
									</Button>
								</Grid>
							</Grid>
						</Modal.Actions>
					</form>
				</Modal>

				<Snackbar
					open={openAlertMessage}
					autoHideDuration={2500}
					onClose={handleClose}
				>
					<Alert severity={messageType} onClose={handleClose}>
						{message}
					</Alert>
				</Snackbar>
			</>
			<ErrorModal onHide={() => setError(null)} error={error} />
		</>
	);
};
